import { formatGlAllocationsLabel } from "./glCodes";

/** CSV export: YYYY-MM-DD like the table, or "-" when missing. */
export function formatInvoiceDateForExport(d) {
  if (d == null || d === "") return "-";
  if (typeof d === "string") {
    const ymd = d.slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : "-";
  }
  if (d instanceof Date && !Number.isNaN(d.getTime())) {
    return d.toISOString().slice(0, 10);
  }
  return "-";
}

export function getCurrentInvoiceMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** Last calendar day of the current invoice month (YYYY-MM-DD), for date input max. */
export function getCurrentInvoiceMonthEndDate() {
  const ym = getCurrentInvoiceMonth();
  const [y, m] = ym.split("-").map(Number);
  const lastDay = new Date(y, m, 0).getDate();
  return `${ym}-${String(lastDay).padStart(2, "0")}`;
}

/**
 * True when the invoice date/month is after the current calendar month.
 * Accepts YYYY-MM or YYYY-MM-DD. Invalid values return false.
 */
export function isFutureInvoiceMonth(ymdOrYm) {
  const s = String(ymdOrYm || "").trim();
  const month = s.length >= 7 ? s.slice(0, 7) : "";
  if (!/^\d{4}-\d{2}$/.test(month)) return false;
  return month > getCurrentInvoiceMonth();
}

const MONTH_SHORT = [
  "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
  "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
];

export function formatMonthShortYm(ym) {
  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return ym;
  const [y, m] = ym.split("-");
  return `${MONTH_SHORT[Number(m) - 1]} ${y}`;
}

export function formatMonthLongYm(ym) {
  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return ym;
  const [y, m] = ym.split("-");
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  return `${monthNames[Number(m) - 1]} ${y}`;
}

/** Tooltip for closed billing accounts on invoice table rows. */
export function closedAccountTooltip(inv) {
  if (!inv || inv.account_is_active !== false) return null;
  const month = inv.closed_effective_month
    ? String(inv.closed_effective_month).slice(0, 7)
    : "";
  const label = /^\d{4}-\d{2}$/.test(month) ? formatMonthLongYm(month) : null;
  return label ? `Closed ${label}` : "Closed account";
}

export function computeMrcChange(priorMrc, currentMrc) {
  const prior = priorMrc != null ? Number(priorMrc) : null;
  const current = currentMrc != null ? Number(currentMrc) : null;
  if (prior == null && current == null) return { dollars: 0, percent: null };
  if (prior == null) return { dollars: current ?? 0, percent: null };
  if (current == null) return { dollars: -(prior ?? 0), percent: null };
  const dollars = Math.round((current - prior) * 100) / 100;
  const percent =
    prior !== 0 ? Math.round((dollars / prior) * 1000) / 10 : null;
  return { dollars, percent };
}

/**
 * Sort key for Send to AP column display states:
 * 0 Awaiting (missing) → 1 dash → 2 Send button → 3 sent date.
 */
export function sendToApSortRank(inv) {
  if (inv?.row_kind === "missing") return 0;
  if (inv?.id == null) return 1;
  if (inv?.email_sent_at) return 3;
  return 2;
}

/** Compare invoices for Send to AP column sort (groups statuses, then dates). */
export function compareSendToAp(a, b, direction = "asc") {
  const rankA = sendToApSortRank(a);
  const rankB = sendToApSortRank(b);
  if (rankA !== rankB) {
    return direction === "asc" ? rankA - rankB : rankB - rankA;
  }
  let dateA = "";
  let dateB = "";
  if (rankA === 0) {
    dateA = a?.prior_invoice_date ? String(a.prior_invoice_date) : "";
    dateB = b?.prior_invoice_date ? String(b.prior_invoice_date) : "";
  } else if (rankA === 3) {
    dateA = a?.email_sent_at ? String(a.email_sent_at) : "";
    dateB = b?.email_sent_at ? String(b.email_sent_at) : "";
  }
  return direction === "asc"
    ? dateA.localeCompare(dateB)
    : dateB.localeCompare(dateA);
}

/** True when month-view MRC / Total / Credit / Invoice No. cells show "—". */
export function isMonthAmountEmpty(inv) {
  return inv?.row_kind === "missing" || (inv?.row_kind === "new" && inv?.id == null);
}

/**
 * Sort month amount columns: empty "—" rows together, then numeric values.
 * @param {Function} getValue - (inv) => number|null
 */
export function compareMonthAmount(a, b, getValue, direction = "asc") {
  const emptyA = isMonthAmountEmpty(a);
  const emptyB = isMonthAmountEmpty(b);
  if (emptyA !== emptyB) {
    // Asc: dashes first; Desc: amounts first (dashes last)
    if (direction === "asc") return emptyA ? -1 : 1;
    return emptyA ? 1 : -1;
  }
  if (emptyA) return 0;
  const numA = Number(getValue(a)) || 0;
  const numB = Number(getValue(b)) || 0;
  return direction === "asc" ? numA - numB : numB - numA;
}

/**
 * Sort Change column: "—" (missing) → New → dollar changes.
 */
export function compareMrcChange(a, b, direction = "asc") {
  const rank = (inv) => {
    if (inv?.row_kind === "missing") return 0;
    if (inv?.row_kind === "new") return 1;
    return 2;
  };
  const rankA = rank(a);
  const rankB = rank(b);
  if (rankA !== rankB) {
    return direction === "asc" ? rankA - rankB : rankB - rankA;
  }
  if (rankA !== 2) return 0;
  const changeA = computeMrcChange(
    a.prior_total_invoice_amount,
    a.total_invoice_amount,
  ).dollars;
  const changeB = computeMrcChange(
    b.prior_total_invoice_amount,
    b.total_invoice_amount,
  ).dollars;
  return direction === "asc" ? changeA - changeB : changeB - changeA;
}

/**
 * Sort prior-month amount: "—" for new rows, then numeric values.
 */
export function comparePriorAmount(a, b, direction = "asc") {
  const emptyA = a?.row_kind === "new";
  const emptyB = b?.row_kind === "new";
  if (emptyA !== emptyB) {
    if (direction === "asc") return emptyA ? -1 : 1;
    return emptyA ? 1 : -1;
  }
  if (emptyA) return 0;
  const numA = Number(a?.prior_monthly_mrc) || 0;
  const numB = Number(b?.prior_monthly_mrc) || 0;
  return direction === "asc" ? numA - numB : numB - numA;
}

/**
 * Sort Note column: empty/missing "—" together, then notes alphabetically.
 */
export function compareNote(a, b, direction = "asc") {
  const noteOf = (inv) => {
    if (inv?.row_kind === "missing") return "";
    return inv?.note != null ? String(inv.note).trim() : "";
  };
  const noteA = noteOf(a);
  const noteB = noteOf(b);
  const emptyA = !noteA;
  const emptyB = !noteB;
  if (emptyA !== emptyB) {
    if (direction === "asc") return emptyA ? -1 : 1;
    return emptyA ? 1 : -1;
  }
  if (emptyA) return 0;
  return direction === "asc"
    ? noteA.localeCompare(noteB)
    : noteB.localeCompare(noteA);
}

/** Absolute MRC % change (matches Change column / backend threshold logic). */
export function absMrcPercentChange(priorMrc, currentMrc) {
  const prior = priorMrc != null ? Number(priorMrc) : null;
  const current = currentMrc != null ? Number(currentMrc) : null;
  if (prior == null || current == null) return null;
  if (prior === 0) return current !== 0 ? 100 : 0;
  const { percent } = computeMrcChange(prior, current);
  return percent != null ? Math.abs(percent) : null;
}

/**
 * Whether Generate note should appear for this invoice row.
 * Requires tenant automation enabled and |total amount % change| >= threshold
 * (same basis as the Change column).
 */
export function invoiceQualifiesForNoteGeneration(inv, settings) {
  if (!settings?.invoiceNoteAutoEnabled) return false;
  if (!inv || inv.id == null) return false;
  if (inv.row_kind === "missing" || inv.row_kind === "new") return false;
  const threshold =
    settings.invoiceMrcChangeThreshold != null
      ? Number(settings.invoiceMrcChangeThreshold)
      : 5;
  const absPercent = absMrcPercentChange(
    inv.prior_total_invoice_amount,
    inv.total_invoice_amount,
  );
  if (absPercent == null || Number.isNaN(threshold)) return false;
  return absPercent >= threshold;
}

export function rowBackgroundClass(rowKind) {
  if (rowKind === "missing") return "bg-white";
  if (rowKind === "new") return "bg-white";
  return "bg-white";
}

/** Table display: MM-DD from YYYY-MM-DD or Date. */
export function formatInvoiceDateShort(d) {
  if (!d) return "—";
  const ymd =
    typeof d === "string"
      ? d.slice(0, 10)
      : d instanceof Date && !Number.isNaN(d.getTime())
        ? d.toISOString().slice(0, 10)
        : String(d).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return "—";
  const [, mm, dd] = ymd.split("-");
  return `${mm}-${dd}`;
}

export function isLargeMrcSwing(dollars, percent) {
  const absDollars = Math.abs(Number(dollars) || 0);
  const absPercent = percent != null ? Math.abs(Number(percent)) : 0;
  return absDollars >= 1000 || absPercent >= 10;
}

const STATUS_MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function ymdTodayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function parseYmdParts(ymd) {
  const s = String(ymd || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, d] = s.split("-").map(Number);
  return { y, m, d };
}

/** e.g. Oct 10 */
export function formatStatusMonthDay(ymd) {
  const p = parseYmdParts(ymd);
  if (!p) return null;
  return `${STATUS_MONTH_SHORT[p.m - 1]} ${p.d}`;
}

function calendarDaysBetween(earlierYmd, laterYmd) {
  const a = parseYmdParts(earlierYmd);
  const b = parseYmdParts(laterYmd);
  if (!a || !b) return null;
  const t0 = Date.UTC(a.y, a.m - 1, a.d);
  const t1 = Date.UTC(b.y, b.m - 1, b.d);
  return Math.round((t1 - t0) / 86400000);
}

export function invoiceReviewCompleted(inv) {
  if (!inv) return false;
  if (inv.mrc_reviewed_at) return true;
  if (inv.note != null && String(inv.note).trim().length > 0) return true;
  return false;
}

/**
 * MRC variance exceeds tenant threshold and review is incomplete.
 * Independent of Invoice Variance Analysis toggle (Mark reviewed path).
 */
export function invoiceNeedsMrcReview(inv, noteSettings) {
  if (!inv || inv.id == null) return false;
  if (inv.row_kind === "missing") return false;
  if (inv.row_kind === "new") return false;
  if (invoiceReviewCompleted(inv)) return false;
  const threshold =
    noteSettings?.invoiceMrcChangeThreshold != null
      ? Number(noteSettings.invoiceMrcChangeThreshold)
      : 5;
  if (Number.isNaN(threshold)) return false;
  const absPercent = absMrcPercentChange(inv.prior_monthly_mrc, inv.monthly_mrc);
  if (absPercent == null) return false;
  return absPercent >= threshold;
}

/** @deprecated Use invoiceNeedsMrcReview / getInvoiceDisplayStatus. Kept for older call sites. */
export function invoiceRowIsFlagged(inv, noteSettings) {
  return invoiceNeedsMrcReview(inv, noteSettings);
}

/** @deprecated Past-due-by-amount is no longer a display status. */
export function isInvoicePastDueAmount() {
  return false;
}

function isInvoiceReceivedRow(inv) {
  if (!inv) return false;
  if (inv.row_kind === "missing") return false;
  if (inv.id == null) return false;
  return true;
}

/**
 * Status key for a row. Priority:
 * Closed → Missing → Expected → New → Credit → Needs review → Received
 */
export function getInvoiceDisplayStatus(inv, noteSettings, options = {}) {
  if (inv?.account_is_active === false) return "closed";

  const today = options.today || ymdTodayLocal();

  if (!isInvoiceReceivedRow(inv)) {
    const expected = inv?.expected_invoice_date
      ? String(inv.expected_invoice_date).slice(0, 10)
      : null;
    const deadline = inv?.expected_deadline
      ? String(inv.expected_deadline).slice(0, 10)
      : null;
    // No established schedule → Expected until first invoice (never Missing).
    if (!expected || !deadline) return "expected";
    if (today <= deadline) return "expected";
    return "missing";
  }

  if (inv.row_kind === "new") return "new";
  if (inv.credit_amount != null && Number(inv.credit_amount) > 0) return "credit";
  if (invoiceNeedsMrcReview(inv, noteSettings)) return "needs_review";
  return "received";
}

/** Days late from expected deadline (not original expected invoice date). */
export function invoiceDaysLate(inv, options = {}) {
  const deadline = inv?.expected_deadline
    ? String(inv.expected_deadline).slice(0, 10)
    : null;
  if (!deadline) return null;
  const today = options.today || ymdTodayLocal();
  const days = calendarDaysBetween(deadline, today);
  if (days == null || days <= 0) return null;
  return days;
}

const INVOICE_STATUS_SORT_RANK = {
  closed: 0,
  missing: 1,
  expected: 2,
  new: 3,
  credit: 4,
  needs_review: 5,
  received: 6,
  // Legacy aliases map to new ranks if stale filters remain in session.
  awaiting: 2,
  flagged: 5,
  past_due: 1,
};

/** Rank matching the Status column (same statuses sort together). */
export function getInvoiceStatusSortRank(inv, noteSettings) {
  return INVOICE_STATUS_SORT_RANK[getInvoiceDisplayStatus(inv, noteSettings)] ?? 6;
}

/**
 * Date under the Status cell for column sort:
 * - Expected / Missing → expected_deadline
 * - Closed → prior_invoice_date
 * - Received / Needs review / Credit → invoice_date
 * kind: 0 Invoice, 1 Expected/Last, 2 none
 */
function getStatusDisplayDateSort(inv, noteSettings) {
  const status = getInvoiceDisplayStatus(inv, noteSettings);
  if (status === "expected" || status === "missing") {
    const raw = inv?.expected_deadline
      ? String(inv.expected_deadline).slice(0, 10)
      : inv?.expected_invoice_date
        ? String(inv.expected_invoice_date).slice(0, 10)
        : "";
    return {
      md: /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw.slice(5) : "",
      kind: 1,
    };
  }
  if (status === "closed") {
    const raw = inv?.prior_invoice_date
      ? String(inv.prior_invoice_date).slice(0, 10)
      : "";
    return {
      md: /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw.slice(5) : "",
      kind: 1,
    };
  }
  if (status === "new") {
    return { md: "", kind: 2 };
  }
  const raw = inv?.invoice_date ? String(inv.invoice_date).slice(0, 10) : "";
  return {
    md: /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw.slice(5) : "",
    kind: 0,
  };
}

/** Status column sort: by displayed MM-DD only (Invoice before Last invoice on same day). */
export function compareInvoiceStatus(a, b, direction = "asc", noteSettings) {
  const aInfo = getStatusDisplayDateSort(a, noteSettings);
  const bInfo = getStatusDisplayDateSort(b, noteSettings);

  const emptyA = !aInfo.md;
  const emptyB = !bInfo.md;
  if (emptyA !== emptyB) {
    if (direction === "asc") return emptyA ? 1 : -1;
    return emptyA ? -1 : 1;
  }
  if (emptyA) return 0;

  if (aInfo.md !== bInfo.md) {
    return direction === "asc"
      ? aInfo.md.localeCompare(bInfo.md)
      : bInfo.md.localeCompare(aInfo.md);
  }

  // Same MM-DD: Invoice … before Last invoice …
  if (aInfo.kind !== bInfo.kind) {
    return direction === "asc"
      ? aInfo.kind - bInfo.kind
      : bInfo.kind - aInfo.kind;
  }
  return 0;
}

export function compareInvoiceGlCode(a, b, direction = "asc") {
  const glOf = (inv) =>
    String(formatGlAllocationsLabel(inv?.gl_allocations, inv?.gl_code || "") || "").trim();
  const glA = glOf(a);
  const glB = glOf(b);
  const emptyA = !glA;
  const emptyB = !glB;
  if (emptyA !== emptyB) {
    if (direction === "asc") return emptyA ? -1 : 1;
    return emptyA ? 1 : -1;
  }
  if (emptyA) return 0;
  return direction === "asc"
    ? glA.localeCompare(glB, undefined, { numeric: true })
    : glB.localeCompare(glA, undefined, { numeric: true });
}

export function computeInvoiceProgressSummary(rows) {
  let expected = 0;
  let received = 0;
  let outstanding = 0;
  let notYetBilled = 0;

  for (const row of rows) {
    expected += 1;
    if (row.row_kind === "present" || (row.row_kind === "new" && row.id != null)) {
      received += 1;
    }
    if (row.row_kind === "missing") {
      outstanding += 1;
      notYetBilled += Number(row.prior_monthly_mrc) || 0;
    }
  }

  return {
    expected,
    received,
    outstanding,
    notYetBilled: Math.round(notYetBilled * 100) / 100,
    progressPct: expected > 0 ? Math.round((received / expected) * 100) : 0,
  };
}

/** Like-for-like MRC change across accounts present in both months. */
export function computeLikeForLikeSummary(rows) {
  let change = 0;
  let accountCount = 0;

  for (const row of rows) {
    if (row.row_kind !== "present") continue;
    if (row.credit_amount != null && Number(row.credit_amount) > 0) continue;
    accountCount += 1;
    const { dollars } = computeMrcChange(row.prior_monthly_mrc, row.monthly_mrc);
    change += dollars;
  }

  return {
    change: Math.round(change * 100) / 100,
    accountCount,
  };
}

export const CURRENT_MONTH_SPOTLIGHT_TD =
  "px-1.5 py-2.5 tabular-nums bg-[var(--band,#eef3fa)] overflow-hidden";

export const CURRENT_MONTH_SPOTLIGHT_TH =
  "px-1.5 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] bg-[var(--band,#eef3fa)] cursor-pointer select-none hover:text-[var(--ink,#1c1c1a)]";

/** Prior month columns — muted reference styling (Compare mode). */
export const PRIOR_MONTH_SPOTLIGHT_TD =
  "px-1.5 py-2.5 tabular-nums text-[var(--muted,#6b6a66)] bg-[var(--band2,#f4f1e8)] overflow-hidden";

export const PRIOR_MONTH_SPOTLIGHT_TH =
  "px-1.5 py-2 text-[11px] uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] bg-[var(--band2,#f4f1e8)]";

/** Footer totals for the MoM comparison table (all filtered rows). */
export function computeComparisonTableTotals(rows) {
  let priorMrc = 0;
  let priorTotalInvoiceAmount = 0;
  let currentMrc = 0;
  let currentTotalInvoiceAmount = 0;
  let credit = 0;

  for (const row of rows) {
    if (row.row_kind !== "new" && row.prior_monthly_mrc != null) {
      priorMrc += Number(row.prior_monthly_mrc) || 0;
    }
    if (row.row_kind !== "new" && row.prior_total_invoice_amount != null) {
      priorTotalInvoiceAmount += Number(row.prior_total_invoice_amount) || 0;
    }
    if (row.row_kind !== "missing") {
      if (!(row.credit_amount != null && Number(row.credit_amount) > 0)) {
        currentMrc += Number(row.monthly_mrc) || 0;
        currentTotalInvoiceAmount += Number(row.total_invoice_amount) || 0;
      }
      if (row.credit_amount != null && Number(row.credit_amount) > 0) {
        credit += Number(row.credit_amount) || 0;
      }
    }
  }

  priorMrc = Math.round(priorMrc * 100) / 100;
  priorTotalInvoiceAmount = Math.round(priorTotalInvoiceAmount * 100) / 100;
  currentMrc = Math.round(currentMrc * 100) / 100;
  currentTotalInvoiceAmount = Math.round(currentTotalInvoiceAmount * 100) / 100;
  credit = Math.round(credit * 100) / 100;
  const { dollars, percent } = computeMrcChange(
    priorMrc,
    currentMrc,
  );

  return {
    priorMrc,
    priorTotalInvoiceAmount,
    currentMrc,
    currentTotalInvoiceAmount,
    credit,
    momChange: dollars,
    momPercent: percent,
    count: rows.length,
  };
}

export function invoiceRowKey(inv) {
  if (inv.id != null) return String(inv.id);
  const base = `${inv.provider}-${inv.billing_account}`;
  if (inv.row_kind === "new") return `new-${base}`;
  return `missing-${base}`;
}

/** Missing rows use id: null; avoid treating null === null as "editing". */
export function isInvoiceRowEditing(editingInvoiceId, inv) {
  if (inv?.id == null || editingInvoiceId == null) return false;
  return editingInvoiceId === inv.id;
}

/** Recompute MoM summary cards from table rows (updates after inline edit/delete). */
export function computeComparisonSummaryFromRows(rows, priorTotalFromApi) {
  let currentTotal = 0;
  let missingCount = 0;
  let newCount = 0;

  for (const row of rows) {
    if (row.row_kind === "missing") {
      missingCount += 1;
    } else {
      if (!(row.credit_amount != null && Number(row.credit_amount) > 0)) {
        currentTotal += Number(row.monthly_mrc) || 0;
      }
      if (row.row_kind === "new") newCount += 1;
    }
  }

  currentTotal = Math.round(currentTotal * 100) / 100;
  const priorTotal = Math.round((Number(priorTotalFromApi) || 0) * 100) / 100;
  const momChange = Math.round((currentTotal - priorTotal) * 100) / 100;
  const momPercent =
    priorTotal > 0 ? Math.round((momChange / priorTotal) * 1000) / 10 : null;

  return { currentTotal, priorTotal, momChange, momPercent, missingCount, newCount };
}

/** Display grouping: provider header, then billing accounts under it. Preserves row order. */
export function shouldGroupInvoiceRowsByProvider(sortKey) {
  return sortKey !== "gl_code" && sortKey !== "row_kind";
}

export function invoiceTableRowGroups(rows, sortKey) {
  if (shouldGroupInvoiceRowsByProvider(sortKey)) {
    return groupInvoiceRowsByProvider(rows);
  }
  const items = Array.isArray(rows) ? rows : [];
  return [{ provider: null, items, accountCount: items.length, receivedCount: 0 }];
}

export function groupInvoiceRowsByProvider(rows) {
  const groups = [];
  const indexByProvider = new Map();
  for (const inv of Array.isArray(rows) ? rows : []) {
    const provider = String(inv.provider || "").trim() || "Unknown";
    let group = indexByProvider.get(provider);
    if (!group) {
      group = { provider, items: [] };
      indexByProvider.set(provider, group);
      groups.push(group);
    }
    group.items.push(inv);
  }
  return groups.map((group) => {
    const receivedCount = group.items.filter(
      (inv) => inv.row_kind !== "missing",
    ).length;
    return {
      ...group,
      accountCount: group.items.length,
      receivedCount,
    };
  });
}
